/**
 * Medimos NOSOTROS si sale audio, en vez de esperar a que Telnyx avise.
 *
 * 🔴 **Por qué no basta `telnyx.warning`.** El SDK emite `31005 LOW_LOCAL_AUDIO`,
 * y es el aviso que enciende el banner rojo, pero es suyo: decide él cuándo y
 * con qué umbral. El 15/10/2026 sí se encendió —y aun así hubo que bajar la
 * grabación de Telnyx y medir la energía del canal del gestor para saber qué
 * pasaba—, porque el aviso dice «poco audio» sin distinguir las dos cosas que
 * importan:
 *
 * - **el gestor no está hablando** — normal, no es un fallo;
 * - **no llega NI RUIDO DE FONDO** — el dispositivo está entregando una pista
 *   muda, y el deudor no va a oír nada en toda la conversación.
 *
 * La diferencia se ve en la energía. Un micrófono vivo en una habitación
 * callada **nunca** da cero: su suelo de ruido mueve `totalAudioEnergy` en cada
 * muestra. Una pista muda la deja **clavada**. Medido el 15/10/2026 sobre
 * grabaciones reales: 1,0e-06 de RMS en las llamadas mudas contra 7,6e-05 de
 * mediana en las ocho jornadas que funcionaron. Tres órdenes de magnitud; no es
 * un umbral delicado.
 *
 * ## La causa que hay que nombrar
 *
 * Una pista muda en Windows es casi siempre **una segunda captura del mismo
 * dispositivo**: otra app —o otra pestaña, o la propia página de ajustes de
 * micrófono de Chrome, cuya barra de nivel ES una captura— tiene el micro
 * cogido, y el SDK recibe silencio digital. Ya se documentó el 24/09/2026 en
 * `asegurarMicrofono`, al probar a dejar la pista del permiso abierta.
 *
 * Por eso el aviso dice «ciérrala», no «elige otro micrófono»: mandar al gestor
 * a comprobar el micrófono en los ajustes de Chrome **provoca** el fallo que
 * cree que está diagnosticando.
 */

/**
 * 🔴 **Un micro muerto y un gestor callado son INDISTINGUIBLES.**
 *
 * Es la medida que decide el diseño entero, y costó una versión anterior de este
 * fichero. Medido el 15/10/2026 en el portátil de Edgar, sobre su propia pata de
 * audio viva, leyendo `media-source` de `chrome://webrtc-internals`:
 *
 * | tramo                  | subida de `totalAudioEnergy` | por segundo |
 * |------------------------|------------------------------|-------------|
 * | 270 s **callado**      | 0,0000000219                 | 8,1e-11     |
 * | 13 s **hablando**      | 0,0873                       | 6,7e-03     |
 *
 * Ocho órdenes de magnitud entre hablar y callar — eso es facilísimo de separar.
 * Lo que **no** se puede separar es un micro callado de un micro muerto: los dos
 * dan prácticamente cero, porque el **supresor de ruido de Chrome entrega
 * silencio digital puro** cuando decide que no hay voz. Su `audioLevel` valía
 * **0 exacto**, no un valor pequeño.
 *
 * La primera versión de esto daba por muerta una pista que no subiera `1e-9` en
 * **5 segundos**. Con 8,1e-11/s, un micro vivo y callado sube 4e-10 en 5 s: por
 * debajo del umbral. Habría pintado el banner rojo cada vez que el gestor se
 * calla a escuchar al deudor, o sea en casi todas las llamadas. Y un aviso que
 * grita en falso deja de mirarse, que es peor que no tenerlo.
 *
 * Así que lo que se detecta **no** es «el micro está muerto» —no se puede— sino
 * **«esta llamada está yendo sin tu voz»**. Que además es lo accionable: es
 * cuando hay que reabrir la sesión. Si el gestor de verdad no ha abierto la boca
 * en 25 segundos de conversación, esa llamada tampoco servía para nada.
 */

/** Cada cuánto se pregunta por las estadísticas. */
export const MUESTREO_MS = 1000;

/**
 * Cuánto tiene que durar una llamada sin voz del gestor para avisar.
 *
 * Larga a propósito. Entre que la llamada pasa a `active` y que el dispositivo
 * entrega las primeras muestras hay un hueco real —el SDK tarda ~3,9 s en abrir
 * el micro, medido el 24/09/2026—, y encima el gestor escucha tanto como habla.
 * Con 5 s avisaba en falso constantemente (ver arriba).
 */
export const VENTANA_MS = 25000;

/**
 * Energía acumulada por debajo de la cual **no ha salido voz**.
 *
 * Calibrado con las dos poblaciones medidas arriba, y queda cómodamente lejos de
 * las dos:
 *
 * - un micro vivo y **callado** 25 s acumula ~2e-09 → **50.000 veces por debajo**;
 * - **un solo segundo** de voz acumula ~6,7e-03 → **67 veces por encima**.
 *
 * O sea: basta con que el gestor diga «buenos días» una vez para no avisar.
 */
export const UMBRAL_DE_VOZ = 1e-4;

/**
 * ¿Está yendo esta llamada sin la voz del gestor?
 *
 * Pura a propósito: es la decisión, y se prueba sin navegador ni temporizadores.
 *
 * @param {Array<{t:number, energia:number, nivel:number, bytes:number, pista:boolean}>} muestras
 *   en orden, desde que empezó la llamada.
 * @returns {null|'sin_pista'|'sin_voz'|'nada_sale'} el motivo, o null si va bien.
 */
export function evaluar(muestras) {
  if (!Array.isArray(muestras) || muestras.length < 2) return null;

  const primera = muestras[0];
  const ultima = muestras[muestras.length - 1];
  if (ultima.t - primera.t < VENTANA_MS) return null;

  // Ni siquiera hay pista de salida: el SDK no capturó nada. Es distinto de que
  // no salga voz y se arregla de otra manera (reabrir la sesión), así que se
  // distingue.
  if (muestras.every((m) => m.pista === false)) return 'sin_pista';

  // Nada sale por la red.
  //
  // ⚠️ Esto NO es redundante con lo de abajo, y tampoco se puede quitar a cambio
  // de mirar solo la energía: el códec de la pata del gestor es **G722, de tasa
  // constante**. Manda los mismos ~64 kbit/s hable o calle, así que `bytesSent`
  // creciendo no prueba que lleve voz —la captura del 15/10 decía 63.630 bits/s
  // con el canal mudo—. Clavado, en cambio, sí prueba que la pata está caída.
  if (ultima.bytes === primera.bytes) return 'nada_sale';

  if (ultima.energia - primera.energia < UMBRAL_DE_VOZ) return 'sin_voz';

  return null;
}

/**
 * ¿Ha hablado el gestor DESDE un instante concreto?
 *
 * Pura, como `evaluar`, y por el mismo motivo: es una decisión y se prueba sin
 * navegador.
 *
 * 🔴 **Es lo único que separa un buzón de una conversación** cuando Telnyx
 * termina su análisis sin oír pitido. Medido sobre las 8 primeras llamadas así
 * (26/10/2026): seis eran contestadores y una era una conversación de 932 s, y
 * **en el instante del aviso las ocho eran idénticas** — mismo `amd_verdict`,
 * misma sospecha de filtro, mismo retardo de 30,1 s. No hay nada en la señal de
 * Telnyx que las distinga. En el audio sí: en un buzón el gestor se calla.
 *
 * Reutiliza `UMBRAL_DE_VOZ`, que está calibrado con margen de sobra para
 * cualquier ventana de este orden: un segundo de voz acumula ~6,7e-03, **67
 * veces por encima**, y un micro vivo y callado 20 s acumula ~1,6e-09, cinco
 * órdenes de magnitud por debajo. Basta con que diga «¿dígame?» una vez.
 *
 * Devuelve `false` cuando no hay muestras suficientes, y eso es lo correcto por
 * defecto: sin datos no se afirma que haya hablado... pero **quien llame a esto
 * tiene que decidir qué hacer con esa duda**. Ver `COLGAR_SIN_VOZ_MS`.
 */
export function haHabladoDesde(muestras, desde) {
  if (!Array.isArray(muestras)) return false;
  const tramo = muestras.filter((m) => m.t >= desde);
  if (tramo.length < 2) return false;
  return tramo[tramo.length - 1].energia - tramo[0].energia >= UMBRAL_DE_VOZ;
}

/**
 * Cuánto se espera, tras el aviso de «sin pitido», antes de colgar por silencio.
 *
 * 20 s, decidido con Edgar el 26/10/2026. Contra los datos reales: los seis
 * buzones se habrían cortado sobre el segundo 50 de llamada en vez de a los 121
 * de mediana, y la conversación de 932 s **no se toca**, porque a esa altura el
 * gestor llevaba rato hablando.
 */
export const COLGAR_SIN_VOZ_MS = 20000;

/**
 * Saca de un `RTCStatsReport` las cuatro cifras que importan.
 *
 * `media-source` es el micrófono tal y como lo ve el navegador —antes de
 * codificar—, y es el único sitio donde se distingue «no habla» de «no hay
 * micrófono». `outbound-rtp` es lo que sale por la red.
 */
export function leerMuestra(report, ahora = Date.now()) {
  let energia = 0;
  let nivel = 0;
  let bytes = 0;
  let pista = false;

  report.forEach((s) => {
    if (s.type === 'media-source' && s.kind === 'audio') {
      pista = true;
      if (typeof s.totalAudioEnergy === 'number') energia = s.totalAudioEnergy;
      if (typeof s.audioLevel === 'number') nivel = s.audioLevel;
    }
    if (s.type === 'outbound-rtp' && s.kind === 'audio' && typeof s.bytesSent === 'number') {
      bytes = s.bytesSent;
    }
  });

  return { t: ahora, energia, nivel, bytes, pista };
}

/** De dónde se saca `getStats` en una llamada del SDK de Telnyx. */
function getStatsDe(call) {
  const pc = call?.peer?.instance;
  if (pc && typeof pc.getStats === 'function') return () => pc.getStats();
  if (typeof call?.getStats === 'function') return () => call.getStats();
  return null;
}

/**
 * Vigila una llamada y avisa cuando lo que sale está mudo.
 *
 * Avisa **una sola vez** por llamada: repetir el aviso cada segundo lo
 * convertiría en ruido y el gestor dejaría de mirarlo.
 *
 * @returns {{parar: () => void, haHablado: (desde:number) => boolean}}
 *   `parar` lo detiene; `haHablado` consulta las muestras que ya lleva tomadas,
 *   que es lo que permite decidir si una llamada sin pitido es un buzón o una
 *   conversación **sin montar un segundo muestreador en paralelo** — dos
 *   lecturas de `getStats` sobre la misma pata darían cifras distintas y la
 *   decisión dependería de cuál mirase cada uno.
 */
export function vigilar(call, { onMudo, muestreoMs = MUESTREO_MS, setIntervalFn = setInterval, clearIntervalFn = clearInterval, ahora = () => Date.now() } = {}) {
  const getStats = getStatsDe(call);
  // Sin `getStats` no hay medición, así que `haHablado` dice que NO ha hablado.
  // Quien decida colgar con eso tiene que tratar el «no sé» como «no cuelgues»,
  // no al revés: ver `COLGAR_SIN_VOZ_MS` y su uso en el Host.
  if (!getStats) return { parar: () => {}, haHablado: () => false, sinMedicion: true };

  let muestras = [];
  let avisado = false;

  const tick = async () => {
    let report;
    try {
      report = await getStats();
    } catch {
      // Una llamada que ya murió tira aquí. No es un fallo de micrófono.
      return;
    }
    if (!report || typeof report.forEach !== 'function') return;

    // ⚠️ El reloj se lee UNA vez por vuelta. Leerlo dos —una al muestrear y otra
    // al cortar— hace que la muestra y el corte usen instantes distintos, y en
    // un test con reloj inyectado agota la serie y vacía la ventana sola: el
    // vigilante deja de avisar y el test aprueba por el motivo equivocado.
    const t = ahora();
    muestras.push(leerMuestra(report, t));
    // La ventana es deslizante: solo se guarda lo justo para cubrirla.
    const corte = t - VENTANA_MS * 2;
    muestras = muestras.filter((m) => m.t >= corte);

    if (avisado) return;
    const motivo = evaluar(muestras);
    if (motivo) {
      avisado = true;
      onMudo?.(motivo);
    }
  };

  const id = setIntervalFn(tick, muestreoMs);
  return {
    parar: () => clearIntervalFn(id),
    haHablado: (desde) => haHabladoDesde(muestras, desde),
    sinMedicion: false,
  };
}
